import { useLang } from '@/i18n';
import css from './index.module.scss';
import { logo, avatar } from './icons';
import { useEffect, useState } from 'react';
import { ItfEmailTip, ItfItem } from './type';
import { initTip, getSubscribe } from './config';
import { sendMultiEmarsysEmail } from './api';
import { subscribeCookie } from 'eco-design-reliance/cookies/subscribeCookie';
import gtm from 'eco-design-reliance/gtm';
import { useRouter } from 'next/router';
import EcoLink from 'eco-design/base/eco-link';
import EcoPicture from 'eco-design/base/eco-picture';
import QuoteWinSimple from '@elem/compCommon/ProductConstructor/BriefGuide/Info/QuoteWinSimple';

const A: React.FC = () => {
    const { locale = '', query, asPath } = useRouter();
    const { popup = '' } = query;
    const { t } = useLang('ces2026.A');
    const [tip, setTip] = useState<ItfEmailTip>(initTip);
    const [input, setInput] = useState<string>('');
    const [visible, setVisible] = useState<boolean>(false);

    const items: ItfItem[] = [
        {
            name: t('sec2_item1_user'),
            text: t('sec2_item1_text'),
        },
        {
            name: t('sec2_item2_user'),
            text: t('sec2_item2_text'),
        },
        {
            name: t('sec2_item3_user'),
            text: t('sec2_item3_text'),
        },
        {
            name: t('sec2_item4_user'),
            text: t('sec2_item4_text'),
        },
    ];

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
    };

    const handleFocus = () => {
        if (tip?.text) {
            setTip(initTip);
        }
    };

    const doValidate = () => {
        const email = input?.trim();
        if (!email) {
            setTip({
                type: 'fail',
                text: t('tip_empty'),
            });
            return false;
        }
        const regEmail = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (!regEmail.test(email)) {
            setTip({
                type: 'fail',
                text: t('tip_illegal'),
            });
            return false;
        }
        return true;
    };

    const handle_GTM = (status: 'success' | 'fail') => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'activity_submit',
            button_name: 'Subscribe',
            status,
            tag: 'Agree',
            __btn_id1: 'ces-20206-A',
            __btn_id2: 'Subscribe',
            __btn_type: 'common',
        });
    };

    const handleSubmit = async () => {
        if (!doValidate()) return;
        const res = await sendMultiEmarsysEmail(
            locale,
            {
                ...getSubscribe(input?.trim()),
                otherContactListIds: [],
            },
            subscribeCookie({
                sector_name: t('title'),
                entry_position: 1,
            }),
        );
        if (res?.code === '0') {
            setTip({ type: 'success', text: t('tip_success') });
            const timer = setTimeout(() => {
                setTip(initTip);
                setInput('');
                clearTimeout(timer);
            }, 3000);
            handle_GTM('success');
        } else {
            setTip({
                type: 'fail',
                text: res?.message as string | 'Something went wrong in the backend!',
            });
            handle_GTM('fail');
        }
    };

    const handle_GTM_246 = () => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'click_get_a_quote',
            product_name: t('sec2_title'),
            position: t('sec2_btn_quote'),
            option_name: 'EcoFlow DELTA Pro Ultra X',
            __btn_id1: 'ces-2025-A',
            __btn_id2: 'Get Expert Help',
            __btn_type: 'common',
        });
    };

    const handleOpen = () => {
        setVisible(true);
        handle_GTM_246();
    };

    useEffect(() => {
        if (popup === '1' && asPath?.endsWith('#dpux')) {
            setVisible(true);
        }
    }, [popup, asPath]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const scene = AniContainer.create({
                triggerElement: '.ces-2026-A',
                triggerHook: 1,
                offset: '150%',
                duration: '50%',
                syncFromTo: [['.ces-2026-A .picture', { y: 0, opacity: 1 }, { y: -240, opacity: 0 }, 0.5]],
            });
            return () => scene?.destroy();
        }
    }, []);

    return (
        <div id="ces-2026-A" className={`ces-2026-A ${css.section} GtmModuleData-section`} data-section-id={'ces-2026-A'}>
            <EcoPicture
                className="picture"
                fetchPriority="high"
                loading="eager"
                webp={[
                    'https://websiteoss.ecoflow.com/upload/image/1765939310999/1.webp',
                    'https://websiteoss.ecoflow.com/upload/image/1765939311198/1_pad.webp',
                    'https://websiteoss.ecoflow.com/upload/image/1765939311407/1_h5.webp',
                ]}
                jpg={[
                    'https://websiteoss.ecoflow.com/upload/image/1765939310420/1.jpg',
                    'https://websiteoss.ecoflow.com/upload/image/1765939310604/1_pad.jpg',
                    'https://websiteoss.ecoflow.com/upload/image/1765939394566/1_h5.jpg',
                ]}
                alt={t('title')}
            />
            <div className="section1">
                {logo}
                <h2 className="title">
                    {t.rich('title', {
                        sup: (tx: string) => <sup>{tx}</sup>,
                    })}
                </h2>
                <div className="input-wrapper">
                    <div className="input-box">
                        <input
                            id="ces-2026-email"
                            name="email"
                            type="text"
                            className="input"
                            placeholder={t('placeholder')}
                            value={input}
                            onChange={handleInput}
                            onFocus={handleFocus}
                        />
                        <button className="btn GtmModuleData-btn-common" data-btn-id={'Subscribe'} onClick={handleSubmit}>
                            {t('subscribe')}
                        </button>
                    </div>
                    {tip?.text && <p className={`tip tip-${tip?.type}`}>{tip?.text}</p>}
                </div>
                {/* <p className="memo">{t('memo')}</p> */}
            </div>
            <div className="section2" id="dpux">
                <div className="img">
                    <EcoPicture
                        jpg={[
                            'https://websiteoss.ecoflow.com/upload/image/1766113788094/A2.png',
                            'https://websiteoss.ecoflow.com/upload/image/1766113788094/A2.png',
                            'https://websiteoss.ecoflow.com/upload/image/1766113787611/A2_m.png',
                        ]}
                        webp={[
                            'https://websiteoss.ecoflow.com/upload/image/1766113804906/A2.webp',
                            'https://websiteoss.ecoflow.com/upload/image/1766113804906/A2.webp',
                            'https://websiteoss.ecoflow.com/upload/image/1766113804644/A2_m.webp',
                        ]}
                        alt={t('sec2_title')}
                    />
                </div>
                <div className="wrapper">
                    <div className="box">
                        <p className="title">{t('sec2_title')}</p>
                        <div className="info-wrapper">
                            <div className="ces-img">
                                <EcoPicture
                                    jpg={['https://websiteoss.ecoflow.com/upload/image/1766113806252/A_ces.png']}
                                    webp={['https://websiteoss.ecoflow.com/upload/image/1766113805201/A_ces.webp']}
                                    alt={t('sec2_title')}
                                />
                            </div>
                            <div className="info-inner">
                                <p className="subtitle">{t('sec2_subtitle')}</p>
                                <p className="desc">{t('sec2_desc')}</p>
                            </div>
                        </div>
                        <div className="btns">
                            <span className="btn quote GtmModuleData-btn-common" data-btn-id="Get Expert Help" onClick={handleOpen}>
                                {t('sec2_btn_quote')}
                            </span>
                            <EcoLink
                                className="btn more GtmModuleData-btn-href"
                                href={`/${locale}/delta-pro-ultra-x?ref=ces2026`}
                                dataLayer={{
                                    event: 'uaEvent',
                                    event_name: 'select_banner',
                                    banner_name: t('sec2_title'),
                                    position: 'banner_2',
                                    button_name: t('sec2_btn_more'),
                                    __btn_id1: 'ces-2026-A',
                                    __btn_id2: `/${locale}/delta-pro-ultra-x?ref=ces2026`,
                                    __btn_type: 'href',
                                }}
                            >
                                {t('sec2_btn_more')}
                            </EcoLink>
                        </div>
                    </div>
                    <div className="items-wrapper">
                        <ul className="items">
                            {[...items, ...items].map((item: ItfItem, k: number) => (
                                <li className="item" key={k}>
                                    <span className="item-avatar">{avatar}</span>
                                    <div className="item-info">
                                        <span className="item-name">{item.name}</span>
                                        <p className="item-text">{item.text}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
            {visible && <QuoteWinSimple callback={() => setVisible(false)} />}
        </div>
    );
};

export default A;
